import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { useNavigate } from "react-router-dom";
import {
  getMarketplaceApps,
  createApp,
  updateApp,
  createInstallation,
  getInstallations,
  uninstallInstallation,
  retryInstallation,
  rolloutUpdate,
} from "../api/marketplaceApi";
import { getEdges } from "../api/edgeApi";

export default function Marketplace() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [edges, setEdges] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(null);
  const [rolloutApp, setRolloutApp] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState("");
  const [addAppOpen, setAddAppOpen] = useState(false);
  const [addAppSaving, setAddAppSaving] = useState(false);
  const [editAppOpen, setEditAppOpen] = useState(false);
  const [editAppSaving, setEditAppSaving] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [editAppForm, setEditAppForm] = useState({
    name: "",
    description: "",
    docker_image: "",
    port: 3000,
    path_prefix: "",
    icon: "",
    required_metrics: "",
  });
  const [newAppForm, setNewAppForm] = useState({
    slug: "",
    name: "",
    description: "",
    docker_image: "registry.valonix.io/",
    port: 3000,
    path_prefix: "",
    icon: "📦",
    required_metrics: "",
  });

  useEffect(() => {
    if (!accessToken) return;

    async function fetch() {
      try {
        setLoading(true);
        const [appsRes, edgesRes, instRes] = await Promise.all([
          getMarketplaceApps(accessToken),
          getEdges(accessToken),
          getInstallations(accessToken),
        ]);
        setApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
        setEdges(Array.isArray(edgesRes) ? edgesRes : edgesRes?.items || edgesRes?.data || []);
        setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || i18n.t("marketplace.loadFailed")); // dil değişince veri yeniden çekilmesin: efekt i18n.t kullanır
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [accessToken]);

  // Devam eden veya (tenant için) başarısız kurulum varken 10 sn'de bir güncelle
  useEffect(() => {
    if (!accessToken) return;
    const hasActive = installations.some((i) =>
      ["pending", "running", "update_pending"].includes(i.status)
    );
    const hasFailedForTenant = me?.global_role !== "superadmin" && installations.some((i) => i.status === "failed");
    if (!hasActive && !hasFailedForTenant) return;
    const iv = setInterval(async () => {
      try {
        const instRes = await getInstallations(accessToken);
        setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
      } catch {
        /* sessizce devam */
      }
    }, 10000);
    return () => clearInterval(iv);
  }, [accessToken, installations, me?.global_role]);

  const handleInstall = async (app) => {
    if (!selectedEdge) {
      toast.error(t("marketplace.selectEdgeFirst"));
      return;
    }
    try {
      setInstalling(app.slug);
      const edge = edges.find((e) => e.id === selectedEdge || e.edgeId === selectedEdge);
      const edgeId = edge?.id || edge?.edgeId || selectedEdge;
      await createInstallation(accessToken, {
        app_id: app.slug,
        edge_id: edgeId,
      });
      toast.success(t("marketplace.installQueued", { name: app.name }));
      const instRes = await getInstallations(accessToken);
      setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("marketplace.installFailed"));
    } finally {
      setInstalling(null);
    }
  };

  const getInstallStatus = (appSlug, edgeId) => {
    return installations.find(
      (i) => i.app_id === appSlug && (i.edge_id === edgeId || i.edge_db_id === edgeId)
    );
  };

  const handleUninstall = async (inst) => {
    if (!inst) return;
    try {
      await uninstallInstallation(accessToken, inst.id);
      toast.success(t("marketplace.uninstallQueued"));
      const instRes = await getInstallations(accessToken);
      setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("marketplace.uninstallFailed"));
    }
  };

  const handleRetry = async (inst) => {
    if (!inst) return;
    try {
      await retryInstallation(accessToken, inst.id);
      toast.success(t("marketplace.retryQueued"));
      const instRes = await getInstallations(accessToken);
      setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("marketplace.retryFailed"));
    }
  };

  const handleRollout = async (app) => {
    try {
      setRolloutApp(app.slug);
      const r = await rolloutUpdate(accessToken, app.slug);
      toast.success(t("marketplace.rolloutDone", { count: r.edges_queued || 0 }));
      const [instRes, appsRes] = await Promise.all([getInstallations(accessToken), getMarketplaceApps(accessToken)]);
      setInstallations(Array.isArray(instRes) ? instRes : instRes?.items || instRes?.data || []);
      setApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("marketplace.rolloutFailed"));
    } finally {
      setRolloutApp(null);
    }
  };

  const getCompletedCountForApp = (appSlug) =>
    installations.filter((i) => i.app_id === appSlug && i.status === "completed").length;

  /** Superadmin: tüm kurulumlar. Tenant admin: sadece başarısız (bildirim için). Başarılı olunca kaybolur. */
  const visibleInstallations = me?.global_role === "superadmin"
    ? installations
    : installations.filter((i) => i.status === "failed");

  const needsRollout = (app) =>
    getCompletedCountForApp(app.slug) > 0 &&
    (app.docker_image !== app.rollout_docker_image || !app.rollout_docker_image);

  const openEditApp = (app) => {
    setEditApp(app);
    setEditAppForm({
      name: app.name || "",
      description: app.description || "",
      docker_image: app.docker_image || "",
      port: app.port ?? 3000,
      path_prefix: app.path_prefix || app.slug || "",
      icon: app.icon || "",
      required_metrics: Array.isArray(app.required_metrics) ? app.required_metrics.join(", ") : (app.required_metrics || ""),
    });
    setEditAppOpen(true);
  };

  const handleUpdateApp = async () => {
    if (!editApp) return;
    if (!editAppForm.docker_image?.trim()) {
      toast.error(t("marketplace.dockerRequired"));
      return;
    }
    try {
      setEditAppSaving(true);
      const payload = {
        name: (editAppForm.name || "").trim() || editApp.slug,
        description: (editAppForm.description || "").trim() || null,
        docker_image: editAppForm.docker_image.trim(),
        port: parseInt(editAppForm.port, 10) || 3000,
        path_prefix: (editAppForm.path_prefix || "").trim() || editApp.slug,
        icon: (editAppForm.icon || "").trim() || null,
        required_metrics: editAppForm.required_metrics
          ? editAppForm.required_metrics.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
      };
      await updateApp(accessToken, editApp.slug, payload);
      toast.success(t("marketplace.updated", { name: editApp.name }));
      setEditAppOpen(false);
      setEditApp(null);
      const appsRes = await getMarketplaceApps(accessToken);
      setApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("marketplace.updateFailed"));
    } finally {
      setEditAppSaving(false);
    }
  };

  const handleAddApp = async () => {
    const slug = (newAppForm.slug || "").trim().toLowerCase().replace(/\s+/g, "-");
    if (!slug) {
      toast.error(t("marketplace.slugRequired"));
      return;
    }
    if (!newAppForm.docker_image?.trim()) {
      toast.error(t("marketplace.dockerRequiredExample"));
      return;
    }
    const path_prefix = (newAppForm.path_prefix || slug).trim() || slug;
    try {
      setAddAppSaving(true);
      const created = await createApp(accessToken, {
        slug,
        name: (newAppForm.name || "").trim() || slug,
        description: (newAppForm.description || "").trim() || null,
        docker_image: newAppForm.docker_image.trim(),
        port: parseInt(newAppForm.port, 10) || 3000,
        path_prefix,
        icon: (newAppForm.icon || "").trim() || null,
        required_metrics: newAppForm.required_metrics
          ? newAppForm.required_metrics.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
      });
      const img = created.docker_image || newAppForm.docker_image.trim();
      const tag = img.includes(":") ? img : `${img}:1.0`;
      toast.success(t("marketplace.added", { name: created.name }), {
        autoClose: 8000,
        bodyStyle: { fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", marginTop: 8 },
      });
      toast.info(t("marketplace.commands", { commands: `docker build -t ${created.slug}:1.0 .\ndocker tag ${created.slug}:1.0 ${tag}\ndocker push ${tag}` }), {
        autoClose: 12000,
        bodyStyle: { fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap" },
      });
      setAddAppOpen(false);
      setNewAppForm({ slug: "", name: "", description: "", docker_image: "registry.valonix.io/", port: 3000, path_prefix: "", icon: "📦", required_metrics: "" });
      const appsRes = await getMarketplaceApps(accessToken);
      setApps(Array.isArray(appsRes) ? appsRes : appsRes?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("marketplace.addFailed"));
    } finally {
      setAddAppSaving(false);
    }
  };

  const statusLabel = (status) => t(`marketplace.status.${status}`, { defaultValue: status });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" color="var(--text-primary)">
        <CircularProgress sx={{ color: "var(--btn-primary)" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, color: "var(--text-primary)" }}>
      <Typography variant="h5" gutterBottom sx={{ color: "var(--text-primary)", fontWeight: 600 }}>
        {t("marketplace.title")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "var(--text-muted)" }}>
        {t("marketplace.subtitle")}
      </Typography>

      <Button variant="outlined" onClick={() => navigate("/marketplace/history")} sx={{ mb: 2, mr: 2 }}>
        {t("marketplace.history.title")}
      </Button>

      {me?.global_role === "superadmin" && (
        <Button
          variant="outlined"
          onClick={() => setAddAppOpen(true)}
          sx={{ mb: 2, mr: 2, borderColor: "#8957e5", color: "#8957e5", "&:hover": { borderColor: "#9b6eef", backgroundColor: "rgba(137,87,229,0.1)" } }}
        >
          {t("marketplace.addApp")}
        </Button>
      )}

      <FormControl
        size="small"
        sx={{
          minWidth: 240,
          mb: 3,
          "& .MuiOutlinedInput-root": { color: "var(--text-primary)" },
          "& .MuiInputLabel-root": { color: "var(--text-muted)" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border)" },
          "& .MuiSvgIcon-root": { color: "var(--text-muted)" },
        }}
      >
        <InputLabel>{t("marketplace.selectEdge")}</InputLabel>
        <Select
          value={selectedEdge}
          label={t("marketplace.selectEdge")}
          onChange={(e) => setSelectedEdge(e.target.value)}
          sx={{ color: "var(--text-primary)" }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "var(--bg-card)",
                color: "var(--text-primary)",
                "& .MuiMenuItem-root": { color: "var(--text-primary)" },
                "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(79,195,247,0.2)" },
              },
            },
          }}
        >
          <MenuItem value="">
            <em>{t("marketplace.select")}</em>
          </MenuItem>
          {edges.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.name || e.edgeId || e.edge_id || e.id} {e.asset?.assetLocation && `- ${e.asset.assetLocation}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>


      <Grid container spacing={2}>
        {apps.map((app) => {
          const inst = selectedEdge
            ? getInstallStatus(app.slug, selectedEdge)
            : null;
          const isPending = inst && ["pending", "running"].includes(inst.status);

          return (
            <Grid item xs={12} sm={6} md={4} key={app.id}>
              <Card
                sx={{
                  backgroundColor: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border)",
                  "& .MuiCardContent-root": { color: "var(--text-primary)" },
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: "var(--text-primary)", fontWeight: 600 }}>
                    {app.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: "var(--text-muted)" }}>
                    {app.description || app.slug}
                  </Typography>
                  {me?.global_role === "superadmin" && (
                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditApp(app)}
                        sx={{ borderColor: "var(--accent-navy-light)", color: "var(--accent-navy-light)", "&:hover": { borderColor: "var(--accent-navy-light)", backgroundColor: "rgba(15, 39, 68, 0.1)" } }}
                      >
                        {t("marketplace.editButton")}
                      </Button>
                      {(app.needs_rollout || (getCompletedCountForApp(app.slug) > 0 && (app.docker_image !== app.rollout_docker_image || !app.rollout_docker_image))) && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={rolloutApp === app.slug}
                          onClick={() => handleRollout(app)}
                          sx={{ borderColor: "#8957e5", color: "#8957e5", "&:hover": { borderColor: "#9b6eef", backgroundColor: "rgba(137,87,229,0.1)" } }}
                        >
                          {rolloutApp === app.slug ? t("marketplace.rollingOut") : t("marketplace.rolloutButton", { count: getCompletedCountForApp(app.slug) })}
                        </Button>
                      )}
                    </Box>
                  )}
                  {app.required_metrics && app.required_metrics.length > 0 && (
                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {app.required_metrics.map((m) => (
                        <Chip
                          key={m}
                          label={m}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: "var(--text-muted)",
                            borderColor: "var(--border)",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {inst?.status === "completed" ? (
                    <>
                      <Chip size="small" label={statusLabel("completed")} sx={{ backgroundColor: "var(--success)", color: "#fff" }} />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleUninstall(inst)}
                        sx={{ borderColor: "var(--danger)", color: "var(--danger)", "&:hover": { borderColor: "var(--danger)", backgroundColor: "rgba(197, 48, 48, 0.1)" } }}
                      >
                        {t("marketplace.uninstall")}
                      </Button>
                    </>
                  ) : inst?.status === "failed" ? (
                    <>
                      <Chip size="small" label={statusLabel("failed")} sx={{ backgroundColor: "var(--danger)", color: "#fff" }} />
                      <Button size="small" variant="contained" onClick={() => handleRetry(inst)} sx={{ backgroundColor: "#8957e5", "&:hover": { backgroundColor: "#9b6eef" } }}>
                        {t("marketplace.retry")}
                      </Button>
                    </>
                  ) : isPending || ["uninstall_pending"].includes(inst?.status) ? (
                    <Chip size="small" label={statusLabel(inst?.status)} sx={{ backgroundColor: "#8957e5", color: "#fff" }} />
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!selectedEdge || installing === app.slug}
                      onClick={() => handleInstall(app)}
                      sx={{
                        backgroundColor: "var(--btn-primary)",
                        color: "#fff",
                        "&:hover": { backgroundColor: "var(--btn-primary-hover)" },
                        "&.Mui-disabled": { backgroundColor: "var(--bg-hover)", color: "var(--text-muted)" },
                      }}
                    >
                      {installing === app.slug ? t("marketplace.installing") : t("marketplace.installHere")}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {apps.length === 0 && !loading && (
        <Typography sx={{ mt: 3, color: "var(--text-muted)" }}>
          {t("marketplace.noApps")}
        </Typography>
      )}

      <Dialog
        open={addAppOpen}
        onClose={() => !addAppSaving && setAddAppOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "var(--bg-card)",
            color: "var(--text-primary)",
            border: "1px solid var(--border)",
          },
        }}
      >
        <DialogTitle sx={{ color: "var(--text-primary)" }}>{t("marketplace.add.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 400, mt: 1 }}>
            <TextField
              label={t("marketplace.add.slug")}
              value={newAppForm.slug}
              onChange={(e) => setNewAppForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder={t("marketplace.add.slugPlaceholder")}
              InputProps={{ sx: { color: "var(--text-primary)" } }}
              InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
            />
            <TextField
              label={t("marketplace.field.name")}
              value={newAppForm.name}
              onChange={(e) => setNewAppForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t("marketplace.add.namePlaceholder")}
              InputProps={{ sx: { color: "var(--text-primary)" } }}
              InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
            />
            <TextField
              label={t("marketplace.field.description")}
              value={newAppForm.description}
              onChange={(e) => setNewAppForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={t("marketplace.add.descriptionPlaceholder")}
              multiline
              InputProps={{ sx: { color: "var(--text-primary)" } }}
              InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
            />
            <TextField
              label={t("marketplace.field.dockerImage")}
              value={newAppForm.docker_image}
              onChange={(e) => setNewAppForm((f) => ({ ...f, docker_image: e.target.value }))}
              placeholder="registry.valonix.io/my-app:1.0"
              InputProps={{ sx: { color: "var(--text-primary)" } }}
              InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
              helperText={t("marketplace.add.dockerHelper")}
            />
            <TextField
              label={t("marketplace.field.port")}
              type="number"
              value={newAppForm.port}
              onChange={(e) => setNewAppForm((f) => ({ ...f, port: e.target.value }))}
              InputProps={{ sx: { color: "var(--text-primary)" } }}
              InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
            />
            <TextField
              label={t("marketplace.field.pathPrefix")}
              value={newAppForm.path_prefix}
              onChange={(e) => setNewAppForm((f) => ({ ...f, path_prefix: e.target.value }))}
              placeholder="/my-app"
              InputProps={{ sx: { color: "var(--text-primary)" } }}
              InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
            />
            <TextField
              label={t("marketplace.add.icon")}
              value={newAppForm.icon}
              onChange={(e) => setNewAppForm((f) => ({ ...f, icon: e.target.value }))}
              placeholder="📦"
              InputProps={{ sx: { color: "var(--text-primary)" } }}
              InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
            />
            <TextField
              label={t("marketplace.field.requiredMetrics")}
              value={newAppForm.required_metrics}
              onChange={(e) => setNewAppForm((f) => ({ ...f, required_metrics: e.target.value }))}
              placeholder="temperature, vibration"
              InputProps={{ sx: { color: "var(--text-primary)" } }}
              InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid var(--border)" }}>
          <Button
            onClick={() => setAddAppOpen(false)}
            disabled={addAppSaving}
            sx={{ color: "var(--text-muted)" }}
          >
            {t("marketplace.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddApp}
            disabled={addAppSaving}
            sx={{ backgroundColor: "#8957e5", "&:hover": { backgroundColor: "#9b6eef" } }}
          >
            {addAppSaving ? t("marketplace.saving") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editAppOpen}
        onClose={() => !editAppSaving && (setEditAppOpen(false), setEditApp(null))}
        PaperProps={{
          sx: {
            backgroundColor: "var(--bg-card)",
            color: "var(--text-primary)",
            border: "1px solid var(--border)",
          },
        }}
      >
        <DialogTitle sx={{ color: "var(--text-primary)" }}>
          {t("marketplace.edit.title")} {editApp && `— ${editApp.name}`}
        </DialogTitle>
        <DialogContent>
          {editApp && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 400, mt: 1 }}>
              <TextField
                label={t("marketplace.field.name")}
                value={editAppForm.name}
                onChange={(e) => setEditAppForm((f) => ({ ...f, name: e.target.value }))}
                InputProps={{ sx: { color: "var(--text-primary)" } }}
                InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
              />
              <TextField
                label={t("marketplace.field.description")}
                value={editAppForm.description}
                onChange={(e) => setEditAppForm((f) => ({ ...f, description: e.target.value }))}
                multiline
                InputProps={{ sx: { color: "var(--text-primary)" } }}
                InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
              />
              <TextField
                label={t("marketplace.field.dockerImage")}
                value={editAppForm.docker_image}
                onChange={(e) => setEditAppForm((f) => ({ ...f, docker_image: e.target.value }))}
                placeholder="registry.valonix.io/my-app:1.1"
                InputProps={{ sx: { color: "var(--text-primary)" } }}
                InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
                helperText={t("marketplace.edit.dockerHelper")}
              />
              <TextField
                label={t("marketplace.field.port")}
                type="number"
                value={editAppForm.port}
                onChange={(e) => setEditAppForm((f) => ({ ...f, port: e.target.value }))}
                InputProps={{ sx: { color: "var(--text-primary)" } }}
                InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
              />
              <TextField
                label={t("marketplace.field.pathPrefix")}
                value={editAppForm.path_prefix}
                onChange={(e) => setEditAppForm((f) => ({ ...f, path_prefix: e.target.value }))}
                InputProps={{ sx: { color: "var(--text-primary)" } }}
                InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
              />
              <TextField
                label={t("marketplace.edit.icon")}
                value={editAppForm.icon}
                onChange={(e) => setEditAppForm((f) => ({ ...f, icon: e.target.value }))}
                InputProps={{ sx: { color: "var(--text-primary)" } }}
                InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
              />
              <TextField
                label={t("marketplace.field.requiredMetrics")}
                value={editAppForm.required_metrics}
                onChange={(e) => setEditAppForm((f) => ({ ...f, required_metrics: e.target.value }))}
                InputProps={{ sx: { color: "var(--text-primary)" } }}
                InputLabelProps={{ sx: { color: "var(--text-muted)" } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid var(--border)" }}>
          <Button
            onClick={() => { setEditAppOpen(false); setEditApp(null); }}
            disabled={editAppSaving}
            sx={{ color: "var(--text-muted)" }}
          >
            {t("marketplace.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateApp}
            disabled={editAppSaving}
            sx={{ backgroundColor: "#8957e5", "&:hover": { backgroundColor: "#9b6eef" } }}
          >
            {editAppSaving ? t("marketplace.saving") : t("marketplace.edit.submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
